/**
 * The read door.
 *
 * Four shapes of read, one of which the renderer actually uses. `projection` is
 * the one call a lens makes: it resolves the private manifest and hands back
 * role-keyed values, so nothing above this line ever sees a property key.
 */

import type { Role, Aggregation } from '@morph/spec';
import type { RoleValues } from '@morph/runtime';
import type { RelationTargetHandle } from './authority.js';
import type { AnchorId, ManifestRef, ObjectId, Revision } from './ids.js';
import type { StoredObject } from './model.js';
import type { Page, Scope, Slice } from './scope.js';

export interface ReadOptions {
  readonly manifest: ManifestRef;
  readonly includeDeleted?: boolean;
}

export interface Traversal {
  readonly manifest: ManifestRef;
  readonly from: readonly ObjectId[];
  readonly via: Role;                            // ROLE_KIND[via] must be 'ref'
  readonly direction: 'out' | 'in' | 'both';
  readonly depth: 1 | 2 | 3 | 4;                 // the spec's Query.traverse bound
  readonly maxNodes: number;
  readonly timeoutMs?: number;
}

export interface TraversalEdge {
  readonly from: ObjectId;
  readonly via: Role;
  readonly to: ObjectId;
  readonly anchor?: AnchorId;
  /**
   * The opaque handle for THIS occurrence, so a rendered relation can be
   * detached without anybody naming an address.
   *
   * Decision 0016: *"Detach consumes the handle attached to the exact rendered
   * occurrence; picker/search mints a target handle for attach."* Traversal is
   * where a relation becomes something on screen, so it is where the detach
   * half is minted.
   */
  readonly target: RelationTargetHandle;
}

export interface TraversalResult {
  readonly nodes: readonly StoredObject[];
  readonly edges: readonly TraversalEdge[];
  readonly depthOf: ReadonlyMap<ObjectId, number>;
  /** Surfaced, never swallowed — node-slot's bounded fallback needs to know. */
  readonly truncated: 'none' | 'depth' | 'max-nodes' | 'timeout';
  readonly cycles: readonly (readonly ObjectId[])[];
}

/** Events carry ids and roles, never values: a subscriber is a second door out
 *  of the store and would otherwise be a content leak with no gate on it. */
export interface StoreEvent {
  readonly scopeKey: string;
  readonly changed: readonly ObjectId[];
  readonly roles: readonly Role[];
  readonly digestRevision: Revision;
  readonly kind: 'values' | 'membership' | 'schema';
}

export type Unsubscribe = () => void;

export interface ObjectReader {
  get(id: ObjectId, opts: ReadOptions): Promise<StoredObject | undefined>;
  getMany(ids: readonly ObjectId[], opts: ReadOptions): Promise<readonly StoredObject[]>;
  count(scope: Scope): Promise<number>;
  query(scope: Scope, page: Page): Promise<Slice<StoredObject>>;
  stream(scope: Scope): AsyncIterable<StoredObject>;
  traverse(t: Traversal): Promise<TraversalResult>;
  /**
   * Resolve the opaque identity carried by one projected relation occurrence.
   *
   * The mirror of `relationTargetFor` on the READ side, and the reason it has
   * to exist: `traverse` deduplicates edges by `from>to`, so a host that named
   * an occurrence by its position in that list opened the following target
   * whenever a slot drew two references to one object.
   *
   * The target is returned only when the reading scope addresses it; a token
   * never widens the manifest boundary.
   */
  occurrenceTarget(scope: Scope, token: string): Promise<ObjectId | undefined>;
  /** The one call the renderer makes. Ref roles expand to nested RoleValues at
   *  bounded depth, which is what node-slot renders. */
  projection(
    scope: Scope,
    page: Page,
    opts?: { readonly expandRefs?: 0 | 1 | 2 | 3 | 4 },
  ): Promise<Slice<RoleValues>>;
  subscribe(scope: Scope, listener: (e: StoreEvent) => void): Unsubscribe;
  /**
   * Fold the WHOLE scope, never a page.
   *
   * `aggregate` has been in the query sublanguage since 0001 — the schema takes
   * it, the validator checks the function against the role's kind, the pool
   * gate canonicalises it as a commuting bag — and nothing ever computed it.
   *
   * It lives here rather than in the renderer for one reason: the renderer
   * holds at most sixty objects. A sum over the sixty it has, printed as a
   * total, is a wrong number presented with total confidence, and there is no
   * way for a reader to tell. The arithmetic belongs to whoever owns the whole
   * set, and that is the store.
   *
   * `groupBy` folds per distinct value instead of over the scope, which is what
   * a board column header needs. There is deliberately no `page` parameter: a
   * paged rollup is the bug this signature exists to make unsayable.
   */
  aggregate(
    scope: Scope,
    specs: readonly AggregateSpec[],
    opts?: { readonly groupBy?: Role },
  ): Promise<AggregateResult>;
}

/** One fold: a function from the closed set, applied to one role. */
export interface AggregateSpec {
  readonly role: Role;
  readonly fn: Aggregation;
}

/**
 * What a fold produced.
 *
 * `n` is the number of objects the fold SAW, and it is not decoration: it is
 * how a reader can tell a total from a sample. It is always the size of the
 * whole scope, because there is no way to ask for anything else.
 *
 * `values` is keyed `"<role>:<fn>"`. A fold over a role no object carries is
 * ABSENT rather than zero — the same rule the rest of the store follows for a
 * property nobody set, and the difference between "these films run no time at
 * all" and "no film here records a runtime".
 */
export interface AggregateResult {
  readonly n: number;
  readonly values: ReadonlyMap<string, number>;
  /** Present only when `groupBy` was asked for: one entry per distinct value,
   *  in the order the group's first member was created. */
  readonly groups?: ReadonlyMap<string, { readonly n: number; readonly values: ReadonlyMap<string, number> }>;
}

/** The key `AggregateResult.values` uses. One function, so a reader and a
 *  writer cannot disagree about the separator. */
export const foldKey = (role: Role, fn: Aggregation): string => `${role}:${fn}`;

/**
 * One coherent point to read from, and the revision that names it.
 *
 * Decision 0027 asks every write to name the read snapshot its plan was based
 * on. Task 0106 found the other half: **a read that spans several awaits is not
 * a point at all.** `loadDocument` has eighteen of them, and a write landing
 * between two of them returns a document THAT NEVER EXISTED — some relations
 * from before it, some from after.
 *
 * @Codex ruled the two are one mechanism rather than two tokens:
 *
 * > *"`loadDocument` must not call ordinary live-store reads while merely
 * > carrying a revision token alongside them. Every await in the traversal must
 * > read through the snapshot's fixed reader."*
 *
 * A revision bolted onto live reads is a token that lies: it would pass every
 * test that checks the token and none that checks the document.
 */
export interface ReadSnapshot {
  /** Answers do not change for the lifetime of the snapshot. */
  readonly reader: ObjectReader;
  /** The authoritative revision OF THAT SAME STATE — what a write built from
   *  this snapshot must name as its commit basis. */
  readonly revision: number;
}
